import React from "react";

type Props = {
  isOpen: boolean;
  onToggle: () => void;
  onReachOutToUs: () => void;
  onClose?: () => void;
  toggleContainerClassName?: string;
  contentContainerClassName?: string;
};

type GuideStep = {
  number: number;
  title: string;
  cardTitle: string;
  body: React.ReactNode;
  footer?: React.ReactNode;
};

const ExternalLink = ({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="underline decoration-white/20 hover:decoration-white/40 transition-colors [overflow-wrap:anywhere]"
  >
    {children}
  </a>
);

const GuideBullets = ({
  items,
  className = "",
}: {
  items: React.ReactNode[];
  className?: string;
}) => (
  <ul
    className={`list-disc pl-5 space-y-1 text-white/70 text-[14px] font-normal leading-[140%] ${className}`}
  >
    {items.map((item, idx) => (
      <li key={idx} className="pl-1">
        {item}
      </li>
    ))}
  </ul>
);

const GuideOrderedList = ({
  items,
  className = "",
}: {
  items: React.ReactNode[];
  className?: string;
}) => (
  <ol
    className={`list-decimal pl-5 space-y-1 text-white/70 text-[14px] font-normal leading-[140%] ${className}`}
  >
    {items.map((item, idx) => (
      <li key={idx} className="pl-1">
        {item}
      </li>
    ))}
  </ol>
);

const GuideCard = ({
  title,
  children,
  footerRight,
}: {
  title: string;
  children: React.ReactNode;
  footerRight?: React.ReactNode;
}) => (
  <div className="w-full rounded-[14px] bg-[#12161C] border border-white/5 shadow-[0_10px_30px_rgba(0,0,0,0.35)] p-5 sm:p-6">
    <p className="font-semibold lg:font-medium text-white text-[16px] leading-[20px] lg:text-[18px] lg:leading-[24px]">
      {title}
    </p>
    <div className="mt-3 text-white/70 text-[14px] font-normal leading-[140%]">
      {children}
    </div>
    {footerRight && (
      <div className="mt-4 flex items-center justify-end text-white/50 text-[14px] leading-[16px]">
        {footerRight}
      </div>
    )}
  </div>
);

export default function MetaAdsFullGuide({
  isOpen,
  onToggle,
  onReachOutToUs,
  toggleContainerClassName = "flex items-center justify-start lg:pl-[52px] w-full",
  contentContainerClassName = "mt-6 w-full px-0 lg:pl-[52px]",
}: Props) {
  const troubleFooter = (
    <span>
      Having trouble?{" "}
      <button
        type="button"
        onClick={onReachOutToUs}
        className="text-[#58F2A9] text-[14px] underline font-normal"
      >
        Reach out to us
      </button>{" "}
      and we&apos;ll help you fix it
    </span>
  );

  const steps: GuideStep[] = [
    {
      number: 1,
      title: "Verify Your Assets",
      cardTitle: "Before you start:",
      body: (
        <div>
          <p className="opacity-80">Make sure you have:</p>
          <GuideBullets
            className="mt-2"
            items={[
              "A Facebook Page",
              <>
                An Instagram (
                <span className="font-semibold text-white/80 text-[14px]">
                  Business or Creator
                </span>
                ) account linked to that Page
              </>,
            ]}
          />
          <p className="mt-3 opacity-80">These are required to run Meta ads.</p>
        </div>
      ),
    },
    {
      number: 2,
      title: "Connect Instagram to Your Facebook Page",
      cardTitle: "On Instagram:",
      body: (
        <div>
          <GuideOrderedList
            items={[
              <>
                Go to your Instagram profile → tap{" "}
                <span className="font-semibold text-white/80 text-[14px]">
                  Edit Profile
                </span>
              </>,
              <>
                Under Profile Information, tap{" "}
                <span className="font-semibold text-white/80 text-[14px]">
                  Page
                </span>
              </>,
              "Select your Facebook account and log in to connect your desired Facebook Page",
            ]}
          />
          <p className="mt-4 font-semibold text-white text-[16px] leading-[20px] lg:text-[18px] lg:leading-[20px]">
            Alternative via Facebook:
          </p>
          <GuideOrderedList
            className="mt-2"
            items={[
              "Go to Facebook Settings → Linked Accounts",
              "Select Instagram and log in to connect the account",
            ]}
          />
        </div>
      ),
    },
    {
      number: 3,
      title: "Set up your Facebook Ad Account",
      cardTitle: "In Facebook Business Manager:",
      body: (
        <div>
          <GuideOrderedList
            items={[
              "Create or select your Business and follow the prompts",
              <>
                Verify your business email – note that Facebook calls it a{" "}
                <span className="font-semibold text-white/80 text-[14px]">
                  “business email”
                </span>{" "}
                but it can be any valid email address
              </>,
            ]}
          />
          <p className="mt-3 opacity-80">
            You can run successful ads with or without a verified blue badge!
          </p>
        </div>
      ),
    },
    {
      number: 4,
      title: "Connect or Create Your Ad Account",
      cardTitle: "In Events Manager:",
      footer: troubleFooter,
      body: (
        <div>
          <GuideOrderedList
            items={[
              "Create a new ad account or connect with an existing one",
              "Choose your time zone, currency, and payment method – save settings so the account is ready for campaigns",
              "Complete any verifications requested by Meta to fully set up your account (we recommend doing these)",
            ]}
          />
        </div>
      ),
    },
    {
      number: 5,
      title: "Set Up Pixel and CAPI Tracking",
      cardTitle: "In Facebook Events Manager:",
      footer: troubleFooter,
      body: (
        <div>
          <GuideOrderedList
            items={[
              <div>
                <p className="font-semibold text-white/80">Access Pixels</p>
                <GuideOrderedList
                  className="mt-2"
                  items={[
                    <>
                      Go to {"https://"}
                      <span className="sm:hidden">
                        <br />
                      </span>
                      <ExternalLink href="https://business.facebook.com/">
                        business.facebook.com/
                      </ExternalLink>
                    </>,
                    "Navigate to Settings",
                    "Under Data Sources, select Datasets & Pixels",
                  ]}
                />
              </div>,
              <div>
                <p className="font-semibold text-white/80">
                  Create or Select a Pixel
                </p>
                <GuideOrderedList
                  className="mt-2"
                  items={[
                    <>
                      To create a new Pixel, click blue{" "}
                      <span className="font-semibold text-white/80 text-[14px]">
                        “Add”
                      </span>{" "}
                      button
                    </>,
                    "Name your Pixel and follow the setup instructions",
                    "Whether using an existing Pixel or one you just created, select it and copy the Pixel ID shown below its name",
                  ]}
                />
              </div>,
              <div>
                <p className="font-semibold text-white/80">Set Up CAPI Token</p>
                <GuideOrderedList
                  className="mt-2"
                  items={[
                    <>
                      Go to {"https://"}
                      <span className="sm:hidden">
                        <br />
                      </span>
                      <ExternalLink href="https://eventsmanager.facebook.com/">
                        eventsmanager.facebook.com/
                      </ExternalLink>
                    </>,
                    "Select the Pixel or Dataset you created (ensure the ID matches the one you copied), then click on “Settings” in the Pixel navigation bar",
                    "Scroll to Conversions API section",
                    "Click Generate Access Token (or copy existing token) with Set up with Dataset Quality API selected",
                    "Copy the generated token",
                  ]}
                />
              </div>,
              <div>
                <p className="font-semibold text-white/80">
                  Enter Details in Pitchplaylists Meta Ads Builder
                </p>
                <GuideOrderedList
                  className="mt-2"
                  items={[
                    "If Facebook was connected previously on Pitchplaylists Meta Ads Builder, reconnect to fetch the latest updates",
                    "Ensure all 4 fields are filled: Facebook Page, Instagram profile, Ad Account, and Pixel",
                    <>
                      If the CAPI token hasn’t been filled in and saved, you can
                      <span className="sm:hidden">
                        <br />
                      </span>{" "}
                      optionally add it in the provided field after selecting
                      your pixel from the dropdown to improve tracking
                      performance.
                    </>,
                    <>
                      Click the “Save for future use” button to activate
                      accurate tracking for all campaigns’ smart links with
                      <span className="sm:hidden">
                        <br />
                      </span>{" "}
                      selected Pixel ID
                    </>,
                  ]}
                />
              </div>,
            ]}
          />
          <p className="mt-4 opacity-80">
            And now you&apos;re ready to move on to the most exciting part:
            setting up the ad campaign!
          </p>
        </div>
      ),
    },
    {
      number: 6,
      title: "Start Your Campaign",
      cardTitle: "Ready to launch:",
      body: (
        <div>
          <GuideOrderedList
            items={[
              <>
                Click{" "}
                <span className="font-semibold text-white/80 text-[14px]">
                  “New Campaign”
                </span>
              </>,
              "Follow the campaign setup steps to complete your ad",
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="w-full">
      <div className="flex flex-col w-full items-start justify-center gap-2.5 relative flex-[0_0_auto]">
        <div className={toggleContainerClassName}>
          <button
            onClick={onToggle}
            className={`inline-flex px-1 lg:pt-2 items-center gap-2 text-sm leading-[18.2px] transition-colors ${
              isOpen ? "text-variable-collection-green2" : "text-white/70"
            }`}
          >
            {isOpen ? "Hide full guide" : "View full guide"}
            {isOpen ? (
              <svg
                className="w-4 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 15l7-7 7 7"
                />
              </svg>
            ) : (
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className={contentContainerClassName}>
          <div className="flex flex-col gap-10">
            {steps.map((step) => (
              <article
                key={step.number}
                className="relative self-stretch w-full flex-[0_0_auto]"
              >
                <div className="grid grid-cols-[28px_1fr] md:grid-cols-[32px_1fr] gap-x-3 md:gap-x-4 gap-y-3 w-full items-start">
                <div className="bg-[#58F2A9] rounded-full flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 mt-0 md:mt-0.5 self-start">
                  <span className="font-medium text-[18px] sm:text-[20px] text-black leading-none">
                    {step.number}
                  </span>
                </div>
                  <h3 className="font-semibold lg:font-normal text-[16px] lg:text-[20px] text-left leading-[20px] lg:leading-[26px] text-variable-collection-white tracking-[0]">
                    {step.title}
                  </h3>

                  <div className="col-span-2 md:col-span-1 md:col-start-2">
                    <GuideCard title={step.cardTitle} footerRight={step.footer}>
                      {step.body}
                    </GuideCard>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
